import { useForm } from 'hooks/useForm';
import { useNavigate } from 'react-router-dom';
import { ChangeEvent, FormEvent, useCallback, useState } from 'react';
import { useAppDispatch } from 'app/hooks';
import { setErrorMessage, setSuccessMessage } from 'store/toast-slice';
import { ServerErrorType } from 'types/server-responses';
import { trimValues } from 'utils/object-helpers';
import { useWorkspace } from 'hooks/useWorkspace';
import { useAddNestedProductCategoryMutation } from '../services';
import {
    NESTED_PRODUCT_CATEGORY_FIELDS_SCHEMA,
    NESTED_PRODUCT_CATEGORY_INITIAL_FIELDS,
} from '../constants/nested-product-categories-fields';
import { NestedProductCategoryDto } from '../types/dtos';
import { NESTED_PRODUCT_CATEGORY_IS_MULTI_SELECT, NESTED_PRODUCT_CATEGORY_STATUS } from '../constants/status';

const useController = () => {
    const { websiteId } = useWorkspace();
    const [isUnlimited, setIsUnlimited] = useState<boolean>(true);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [addNestedProductCategory, { isLoading: isAddNestedProductCategoryLoading }] =
        useAddNestedProductCategoryMutation();

    const {
        formData,
        handleChange: onChange,
        handleSubmit,
        errors,
    } = useForm({
        defaultValues: NESTED_PRODUCT_CATEGORY_INITIAL_FIELDS,
        validationSchema: NESTED_PRODUCT_CATEGORY_FIELDS_SCHEMA,
    });

    const handleSuccess = useCallback(() => {
        dispatch(setSuccessMessage({ key: 'nestedProductCategory.NESTED_PRODUCT_CATEGORY_ADDED' }));
    }, [dispatch]);

    const handleErrors = useCallback(
        (error: ServerErrorType) => {
            const code = error.data?.errorCode;
            dispatch(setErrorMessage({ key: code }));
        },
        [dispatch],
    );

    const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
        setIsUnlimited(event.target.checked);
    };

    const getPayload = useCallback(() => {
        const data = { ...formData };

        data.isActive = data.isActive === NESTED_PRODUCT_CATEGORY_STATUS.ACTIVE ? true : false;
        data.websiteId = websiteId;
        data.isMultiSelect = data.isMultiSelect === NESTED_PRODUCT_CATEGORY_IS_MULTI_SELECT.YES ? true : false;
        if (isUnlimited || !data.isMultiSelect) {
            data.maxSelection = null;
        } else {
            data.maxSelection = Number(data.maxSelection);
        }
        return trimValues(data) as NestedProductCategoryDto;
    }, [formData, websiteId, isUnlimited]);

    const onSubmit = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            handleSubmit(async () => {
                try {
                    const payload = getPayload();

                    await addNestedProductCategory(payload).unwrap();

                    handleSuccess();
                    navigate(-1);
                } catch (error) {
                    handleErrors(error as ServerErrorType);
                }
            });
        },
        [addNestedProductCategory, getPayload, handleErrors, handleSubmit, handleSuccess, navigate],
    );

    const onCancel = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    return {
        formData,
        onSubmit,
        onCancel,
        onChange,
        onCheckChange: handleCheckboxChange,
        isUnlimited,
        isAddNestedProductCategoryLoading: isAddNestedProductCategoryLoading,
        errors,
    };
};

export default useController;
